import { formatCurrencyAmount } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { useUserData } from '@shared/hooks/useUser';
import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Mismatch } from '../models';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

// 🎯 Función utilitaria para formatear montos
const formatAmount = (amount: number, currency: string): string => formatCurrencyAmount(amount, currency || 'EUR');

// 🎯 Configuración de variantes de badge por tipo de descuadre
type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'transparent-blue'
  | 'transparent-orange'
  | 'transparent-green';

const getMismatchTypeVariant = (type: string): BadgeVariant => {
  switch (type) {
    case 'pedido_sin_cobrar':
    case 'pedido_sin_pagar':
      return 'transparent-orange'; // warning
    case 'courier_pago_de_mas':
    case 'cliente_cobro_de_mas':
      return 'transparent-blue'; // info
    case 'diferencia_entre_courier_cliente':
    case 'ambos_incompletos_con_diferencia':
      return 'destructive';
    case 'otro':
    default:
      return 'default';
  }
};

export const useAgGridColumns = () => {
  const { t } = useTranslation('codMismatches');
  const { data: userData } = useUserData();
  const teamCurrency = userData?.settings?.currency?.value || (userData?.team as any)?.currency || 'EUR';

  const columnDefs: ColDef<Mismatch>[] = useMemo(() => {
    const columns: ColDef<Mismatch>[] = [
      {
        field: 'ref',
        headerName: t('table.ref', 'Referencia'),
        minWidth: 130,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.ref ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm font-medium text-foreground">{params.data?.ref ?? '-'}</span>
        ),
      },
      {
        field: 'order_id',
        headerName: t('table.order_id', 'ID Pedido'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.order_id ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{params.data?.order_id ?? '-'}</span>
        ),
      },
      {
        field: 'tracking_number',
        headerName: t('table.tracking_number', 'Tracking'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.tracking_number ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{params.data?.tracking_number ?? '-'}</span>
        ),
      },
      {
        field: 'shop_name',
        headerName: t('table.shop_name', 'Tienda'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.shop_name ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{params.data?.shop_name ?? '-'}</span>
        ),
      },
      {
        field: 'team_name',
        headerName: t('table.team_name', 'Cliente'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.team_name ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{params.data?.team_name ?? '-'}</span>
        ),
      },
      {
        field: 'courier_name',
        headerName: t('table.courier_name', 'Courier'),
        minWidth: 140,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.courier_name ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{params.data?.courier_name ?? '-'}</span>
        ),
      },
      {
        field: 'order_total',
        headerName: t('table.order_total', 'Total Pedido'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.order_total ?? 0,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => {
          const amount = params.data?.order_total ?? 0;
          const currency = teamCurrency;
          return <span className="text-sm font-medium text-foreground">{formatAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'courier_total',
        headerName: t('table.courier_total', 'Total Courier'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.courier_total ?? 0,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => {
          const amount = params.data?.courier_total ?? 0;
          const currency = teamCurrency;
          return <span className="text-sm font-medium text-foreground">{formatAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'client_total',
        headerName: t('table.client_total', 'Total Cliente'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.client_total ?? 0,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => {
          const amount = params.data?.client_total ?? 0;
          const currency = teamCurrency;
          return <span className="text-sm font-medium text-foreground">{formatAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'balance',
        headerName: t('table.balance', 'Balance'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.balance ?? 0,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => {
          const balance = params.data?.balance ?? 0;
          const currency = teamCurrency;
          const isNegative = balance < 0;
          const isPositive = balance > 0;
          return (
            <span
              className={`text-sm font-medium ${
                isNegative ? 'text-destructive' : isPositive ? 'text-success' : 'text-foreground'
              }`}
            >
              {formatAmount(balance, currency)}
            </span>
          );
        },
      },
      {
        field: 'mismatch_type',
        headerName: t('table.mismatch_type', 'Tipo'),
        minWidth: 200,
        sortable: false,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.mismatch_type ?? '-',
        cellRenderer: (params: ICellRendererParams<Mismatch>) => {
          const type = params.data?.mismatch_type;
          if (!type) return '-';

          const variant = getMismatchTypeVariant(type);
          const label = t(`mismatch_types.${type}`, type);

          return <Badge variant={variant}>{label}</Badge>;
        },
      },
      {
        field: 'last_movement',
        headerName: t('table.last_movement', 'Último Movimiento'),
        minWidth: 160,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.last_movement ?? null,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{formatDateTime(params.data?.last_movement)}</span>
        ),
      },
      {
        field: 'delivered_at',
        headerName: t('table.delivered_at', 'Fecha Entrega'),
        minWidth: 160,
        sortable: true,
        valueGetter: (params: ValueGetterParams<Mismatch>) => params.data?.delivered_at ?? null,
        cellRenderer: (params: ICellRendererParams<Mismatch>) => (
          <span className="text-sm text-foreground">{formatDateTime(params.data?.delivered_at)}</span>
        ),
      },
    ];

    return columns;
  }, [t, teamCurrency]);

  return { columnDefs };
};
